import { defineSlotRecipe } from "@chakra-ui/react";

export const toastRecipe = defineSlotRecipe({
  slots: [
    "base",
    "content",
    "title",
    "description",
    "icon",
    "closeButton",
    "closeIcon",
    "progressTrack",
    "progressIndicator",
    "loadingComponent",
  ],
  base: {
    base: {
      display: "flex",
      alignItems: "flex-start",
      gap: 3,
      p: 4,
      borderRadius: "lg",
      shadow: "lg",
      borderWidth: "1px",
      borderColor: "neutral.200",
      bg: "white",
      maxW: "400px",
      minW: "300px",
      position: "relative",
      overflow: "hidden",
    },
    content: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      flex: 1,
      minW: 0,
    },
    title: {
      fontSize: "sm",
      fontWeight: "semibold",
      color: "neutral.900",
      lineHeight: "1.2",
    },
    description: {
      fontSize: "sm",
      color: "neutral.600",
      lineHeight: "1.4",
    },
    icon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      boxSize: 5,
    },
    closeButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      boxSize: 6,
      borderRadius: "md",
      bg: "transparent",
      color: "neutral.500",
      cursor: "pointer",
      _hover: {
        bg: "neutral.100",
        color: "neutral.700",
      },
    },
    closeIcon: {
      boxSize: 4,
    },
    progressTrack: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      h: "2px",
      bg: "neutral.200",
    },
    progressIndicator: {
      h: "100%",
      bg: "primary.500",
      transition: "width 0.1s linear",
    },
    loadingComponent: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSize: 5,
      flexShrink: 0,
    },
  },
  variants: {
    color: {
      default: {
        base: {
          borderColor: "neutral.200",
          bg: "white",
        },
        icon: {
          color: "neutral.500",
        },
      },
      primary: {
        base: {
          borderColor: "primary.200",
          bg: "primary.50",
        },
        icon: {
          color: "primary.500",
        },
        title: {
          color: "primary.900",
        },
        description: {
          color: "primary.700",
        },
      },
      secondary: {
        base: {
          borderColor: "neutral.300",
          bg: "neutral.50",
        },
        icon: {
          color: "neutral.600",
        },
        title: {
          color: "neutral.900",
        },
        description: {
          color: "neutral.700",
        },
      },
      success: {
        base: {
          borderColor: "green.200",
          bg: "green.50",
        },
        icon: {
          color: "green.500",
        },
        title: {
          color: "green.900",
        },
        description: {
          color: "green.700",
        },
      },
      warning: {
        base: {
          borderColor: "orange.200",
          bg: "orange.50",
        },
        icon: {
          color: "orange.500",
        },
        title: {
          color: "orange.900",
        },
        description: {
          color: "orange.700",
        },
      },
      danger: {
        base: {
          borderColor: "red.200",
          bg: "red.50",
        },
        icon: {
          color: "red.500",
        },
        title: {
          color: "red.900",
        },
        description: {
          color: "red.700",
        },
      },
    },
    variant: {
      solid: {
        base: {
          borderWidth: 0,
        },
        default: {
          base: {
            bg: "neutral.900",
            color: "white",
          },
          title: {
            color: "white",
          },
          description: {
            color: "neutral.200",
          },
          closeButton: {
            color: "neutral.300",
            _hover: {
              bg: "neutral.700",
              color: "white",
            },
          },
        },
        primary: {
          base: {
            bg: "primary.500",
            color: "white",
          },
          title: {
            color: "white",
          },
          description: {
            color: "primary.100",
          },
          closeButton: {
            color: "primary.200",
            _hover: {
              bg: "primary.600",
              color: "white",
            },
          },
        },
        secondary: {
          base: {
            bg: "neutral.600",
            color: "white",
          },
          title: {
            color: "white",
          },
          description: {
            color: "neutral.200",
          },
          closeButton: {
            color: "neutral.300",
            _hover: {
              bg: "neutral.700",
              color: "white",
            },
          },
        },
        success: {
          base: {
            bg: "green.500",
            color: "white",
          },
          title: {
            color: "white",
          },
          description: {
            color: "green.100",
          },
          closeButton: {
            color: "green.200",
            _hover: {
              bg: "green.600",
              color: "white",
            },
          },
        },
        warning: {
          base: {
            bg: "orange.500",
            color: "white",
          },
          title: {
            color: "white",
          },
          description: {
            color: "orange.100",
          },
          closeButton: {
            color: "orange.200",
            _hover: {
              bg: "orange.600",
              color: "white",
            },
          },
        },
        danger: {
          base: {
            bg: "red.500",
            color: "white",
          },
          title: {
            color: "white",
          },
          description: {
            color: "red.100",
          },
          closeButton: {
            color: "red.200",
            _hover: {
              bg: "red.600",
              color: "white",
            },
          },
        },
      },
      bordered: {
        base: {
          borderWidth: "2px",
        },
      },
      flat: {
        base: {
          borderWidth: 0,
          shadow: "none",
        },
      },
    },
    radius: {
      none: {
        base: {
          borderRadius: 0,
        },
      },
      sm: {
        base: {
          borderRadius: "sm",
        },
      },
      md: {
        base: {
          borderRadius: "md",
        },
      },
      lg: {
        base: {
          borderRadius: "lg",
        },
      },
      full: {
        base: {
          borderRadius: "full",
        },
      },
    },
    size: {
      sm: {
        base: {
          p: 3,
          gap: 2,
        },
        title: {
          fontSize: "xs",
        },
        description: {
          fontSize: "xs",
        },
        icon: {
          boxSize: 4,
        },
        closeButton: {
          boxSize: 5,
        },
      },
      md: {
        base: {
          p: 4,
          gap: 3,
        },
        title: {
          fontSize: "sm",
        },
        description: {
          fontSize: "sm",
        },
        icon: {
          boxSize: 5,
        },
        closeButton: {
          boxSize: 6,
        },
      },
      lg: {
        base: {
          p: 5,
          gap: 4,
        },
        title: {
          fontSize: "md",
        },
        description: {
          fontSize: "md",
        },
        icon: {
          boxSize: 6,
        },
        closeButton: {
          boxSize: 7,
        },
      },
    },
  },
  defaultVariants: {
    color: "default",
    variant: "flat",
    radius: "lg",
    size: "md",
  },
});
